import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NewRelicService } from '../services/newrelic.service';

@Injectable()
export class NewRelicHttpInterceptor implements HttpInterceptor {
  private readonly _newRelic = inject(NewRelicService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this._newRelic.isReady()) {
      return next.handle(request);
    }

    const startTime = performance.now();
    const requestDetails = {
      url: request.url,
      method: request.method,
      urlWithParams: request.urlWithParams,
    };

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            const duration = Math.round(performance.now() - startTime);
            this._newRelic.addPageAction('APICallSuccess', {
              ...requestDetails,
              statusCode: event.status,
              statusText: event.statusText,
              duration,
              contentType: event.headers.get('content-type') ?? 'unknown',
            });
            this._newRelic.trackMetric(
              `API_${request.method}_Duration`,
              duration,
            );
          }
        },
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            const duration = Math.round(performance.now() - startTime);
            this._newRelic.addPageAction('APICallFailure', {
              ...requestDetails,
              statusCode: error.status,
              statusText: error.statusText,
              errorMessage: error.message,
              duration,
            });

            let apiEndpoint = request.url;
            try {
              apiEndpoint = new URL(request.url).pathname;
            } catch {
              // keep original url for relative paths
            }

            this._newRelic.noticeError(new Error('API Error'), {
              errorType: 'HttpError',
              httpMethod: request.method,
              apiUrl: request.url,
              apiEndpoint,
              statusCode: error.status,
              statusText: error.statusText,
              errorMessage: error.message,
              duration,
              pageUrl: window.location.href,
              pagePath: window.location.pathname,
            });
          }
        },
      }),
    );
  }
}
